import { useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Badge, Menu, Table, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'

import { useParams } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import AvatarGroup from 'src/components/DataDisplay/AvatarGroup/AvatarGroup'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import Empty from 'src/components/Feedback/Empty/Empty'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_DOCUMENT_DOWNLOAD_URL } from 'src/graphql/documents.graphql'
import {
  GET_APPLICANT_TRAININGS,
  REMOVE_LUMA_APPLICANT_COURSE,
} from 'src/graphql/trainings.graphql'
import { TrainingStatus } from 'src/graphql/types/trainings'
import IconThreeDots from 'src/icons/IconThreeDots'
import { formatSnakeValue } from 'src/lib/formatters'
import { downloadFile } from 'src/utils/downloadFile'

type UnifiedRow = {
  key: string
  name: string
  provider: 'LUMA' | 'TRAINIX' | null
  subType: 'Lesson' | 'Course' | 'eNugget' | 'L. Path'
  duration?: string | null
  status: string
  employee?: { firstName?: string | null; lastName?: string | null } | null
  createdAt: string
  endDate?: string | null
  deadline?: string | null
  parentCourseName?: string | null
  applicantCourseId?: string | null
  documents?: { documentId: string; fileName?: string | null }[]
}

const normalizeProvider = (
  provider: string | null | undefined
): UnifiedRow['provider'] => (provider === 'LUMA' ? 'LUMA' : 'TRAINIX')

const subTypeForLesson = (
  provider: UnifiedRow['provider']
): UnifiedRow['subType'] => (provider === 'LUMA' ? 'eNugget' : 'Lesson')

const subTypeForCourse = (
  provider: UnifiedRow['provider']
): UnifiedRow['subType'] => (provider === 'LUMA' ? 'L. Path' : 'Course')

const TrainingTable = () => {
  const params = useParams()
  const applicantId = params.id

  const { data, loading } = useQuery(GET_APPLICANT_TRAININGS, {
    variables: { applicantId },
  })

  const [pendingRemoval, setPendingRemoval] = useState<{
    applicantCourseId: string
    courseName: string
  } | null>(null)

  const [removeLumaApplicantCourse, { loading: removing }] = useMutation(
    REMOVE_LUMA_APPLICANT_COURSE,
    {
      onCompleted: () => {
        toast('Course removed', 'success')
      },
      onError: (error) => {
        toast(error.message, 'error')
      },
      refetchQueries: [
        { query: GET_APPLICANT_TRAININGS, variables: { applicantId } },
      ],
    }
  )

  const [fetchDocumentUrl] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_URL, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const download = res?.documentDownloadUrl
      if (!download?.url) {
        toast('Document is not available', 'error')
        return
      }
      downloadFile({
        url: download.url,
        fileName: download.fileName,
        fileType: download.fileType,
      })
    },
    onError: (error) => {
      toast(error.message, 'error')
    },
  })

  const handleViewDocument = (documentId: string) => {
    fetchDocumentUrl({ variables: { id: documentId, applicantId } })
  }

  const lessons = data?.applicantTrainings?.items || []
  const courses = data?.applicantTrainings?.courses || []

  const rows: UnifiedRow[] = [
    ...lessons.map((lesson): UnifiedRow => {
      const provider = normalizeProvider(lesson.training.provider)
      return {
        key: `at-${lesson.applicantTrainingId}`,
        name: lesson.training.name,
        provider,
        subType: subTypeForLesson(provider),
        duration: lesson.training?.duration,
        status: lesson.status,
        employee: lesson.employee,
        createdAt: lesson.createdAt,
        endDate: lesson.endDate,
        deadline: lesson.deadline,
        parentCourseName:
          lesson.applicantCourses?.[0]?.applicantCourse?.course?.name ?? null,
      }
    }),
    ...courses.map((c): UnifiedRow => {
      const provider = normalizeProvider(c.course.provider)
      return {
        key: `ac-${c.applicantCourseId}`,
        name: c.course.name,
        provider,
        subType: subTypeForCourse(provider),
        duration: c.course?.duration,
        status: c.status,
        employee: c.employee,
        createdAt: c.createdAt,
        endDate: c.endDate,
        deadline: c.deadline,
        applicantCourseId: c.applicantCourseId,
        documents: c.documents ?? [],
      }
    }),
  ].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))

  const canRemove = (row: UnifiedRow) =>
    row.provider === 'LUMA' &&
    row.status !== TrainingStatus.COMPLETED &&
    !!row.applicantCourseId

  const hasDocuments = (row: UnifiedRow) => (row.documents?.length ?? 0) > 0

  const hasActions = (row: UnifiedRow) => canRemove(row) || hasDocuments(row)

  const tableRows = loading ? (
    <></>
  ) : (
    rows.map((row) => (
      <Table.Tr key={row.key} className="text-sm text-doubleNickel-gray-900">
        <Table.Td className="text-nowrap py-2 pl-4">
          <div className="flex flex-col">
            {row.name}
            {row.parentCourseName && (
              <div className="text-xs">{row.parentCourseName}</div>
            )}
          </div>
        </Table.Td>
        <Table.Td className="whitespace-nowrap">
          <div className="flex items-center gap-2">
            {row.provider === 'LUMA' ? (
              <Badge size="xs" variant="light" color="orange" title="Luma">
                Luma
              </Badge>
            ) : (
              <Badge size="xs" variant="light" color="blue" title="Trainix">
                Trainix
              </Badge>
            )}
          </div>
        </Table.Td>
        <Table.Td>
          <span>{row.subType}</span>
        </Table.Td>
        <Table.Td>{row.duration ? row.duration + ' min' : '-'}</Table.Td>
        <Table.Td>
          {row.employee ? (
            <AvatarGroup
              avatars={[
                {
                  firstName: row.employee.firstName ?? '',
                  lastName: row.employee.lastName ?? '',
                },
              ]}
            />
          ) : (
            '-'
          )}
        </Table.Td>
        <Table.Td>{dayjs(row.createdAt).format('MM/DD/YYYY')}</Table.Td>
        <Table.Td>
          {row.deadline ? dayjs(row.deadline).format('MM/DD/YYYY') : '-'}
        </Table.Td>
        <Table.Td>
          {row.endDate ? dayjs(row.endDate).format('MM/DD/YYYY') : '-'}
        </Table.Td>
        <Table.Td className="whitespace-nowrap">
          <StyledBadge
            textTransform="capitalize"
            color={
              row.status === TrainingStatus.NOT_STARTED
                ? 'gray'
                : row.status === TrainingStatus.IN_PROGRESS
                ? 'blue'
                : 'green'
            }
          >
            {formatSnakeValue(row.status)}
          </StyledBadge>
        </Table.Td>
        <Table.Td>
          {hasActions(row) && (
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <Tooltip label="Actions" withArrow position="top">
                  <ActionIcon variant="subtle" className="h-5 w-5">
                    <IconThreeDots className="h-3 w-3 fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown className="w-40">
                {row.documents?.map((document) => (
                  <Menu.Item
                    key={document.documentId}
                    className="text-doubleNickel-gray-700"
                    title={document.fileName ?? undefined}
                    onClick={() => handleViewDocument(document.documentId)}
                  >
                    View Document
                  </Menu.Item>
                ))}
                {canRemove(row) && (
                  <Menu.Item
                    className="text-doubleNickel-gray-700"
                    onClick={() =>
                      setPendingRemoval({
                        applicantCourseId: row.applicantCourseId!,
                        courseName: row.name,
                      })
                    }
                  >
                    Remove Course
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Table.Td>
      </Table.Tr>
    ))
  )

  return (
    <>
      <Table highlightOnHover stickyHeader>
        <Table.Thead>
          <Table.Tr className="h-11 border-none bg-doubleNickel-gray-50 text-xs text-doubleNickel-gray-600">
            <Table.Th className="p-4">Course Name</Table.Th>
            <Table.Th className="min-w-24 py-4">Provider</Table.Th>
            <Table.Th className="py-4">Type</Table.Th>
            <Table.Th className="py-4">Duration</Table.Th>
            <Table.Th className="py-4">Assigned By</Table.Th>
            <Table.Th className="py-4">Assigned Date</Table.Th>
            <Table.Th className="py-4">Due Date</Table.Th>
            <Table.Th className="py-4">Completed Date</Table.Th>
            <Table.Th className="min-w-28 py-4">Status</Table.Th>
            <Table.Th className="py-4" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{tableRows}</Table.Tbody>
        <Table.Caption>
          {rows.length === 0 && !loading && (
            <Empty
              title="No Active Trainings"
              subtitle="Assign a training to get started"
            />
          )}
        </Table.Caption>
      </Table>
      <DeleteModal
        title="Remove this course?"
        description={pendingRemoval?.courseName}
        opened={!!pendingRemoval}
        close={() => !removing && setPendingRemoval(null)}
        onDelete={async () => {
          if (!pendingRemoval) return
          try {
            await removeLumaApplicantCourse({
              variables: {
                applicantCourseId: pendingRemoval.applicantCourseId,
              },
            })
          } catch {
            // error already surfaced via mutation onError toast
          } finally {
            setPendingRemoval(null)
          }
        }}
      />
    </>
  )
}

export default TrainingTable
